import { Label, Text as TextComponent } from "@scott/ui";
import { cn } from "@scott/ui/client";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { IconClose, IconTick } from "../../icons";

const textareaVariants = cva("", {
  variants: {
    status: {
      error: "",
      success: "",
      disabled: "disabled",
    },
    size: {
      sm: "text-sm leading-4",
      md: "text-base",
      lg: "text-lg",
    },
  },
  defaultVariants: {
    status: null,
    size: "sm",
  },
});

const textareaMinHeightBySize = {
  sm: "min-h-24",
  md: "min-h-20",
  lg: "min-h-17.5",
} as const;

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {
  placeholder: string;
  label?: string;
  message?: string;
  isRequired?: boolean;
}

const Textarea = ({
  className,
  label,
  status,
  size = "sm",
  isRequired,
  message,
  ref,
  ...props
}: TextareaProps & { ref?: React.Ref<HTMLTextAreaElement> }) => (
  <div className={cn("flex flex-1 flex-col gap-1 font-paragraph", className)}>
    {label ? (
      <Label className={cn("flex flex-row gap-1 py-1")} htmlFor={label}>
        <span
          className={cn(
            props.disabled ? "text-neutral-medium" : "text-neutral-darkest",
            textareaVariants({ size })
          )}
        >
          {label}
        </span>
        {isRequired ? (
          <span
            className={cn(
              props.disabled ? "text-neutral-medium" : "text-neutral-dark",
              textareaVariants({ size })
            )}
          >
            *
          </span>
        ) : null}
      </Label>
    ) : null}
    <div
      className={cn(
        "flex gap-2 border disabled:bg-neutral-light",
        status === "error" && "border-danger-medium",
        status === "success" && "border-success-medium",
        props.disabled && "bg-neutral-light",
        className
      )}
    >
      <textarea
        className={cn(
          "flex w-full bg-background px-3 py-2 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
          textareaVariants({ size }),
          textareaMinHeightBySize[size ?? "sm"]
        )}
        ref={ref}
        {...props}
      />
    </div>
    <div>
      {status === "error" && (
        <span className="flex gap-1">
          <IconClose
            className="mt-2 h-3 w-3 rounded-full text-error"
            size="sm"
          />
          <TextComponent variant="muted">{message}</TextComponent>{" "}
        </span>
      )}
      {status === "success" && (
        <span className="flex gap-1">
          <IconTick className="mt-2 h-3 w-3 text-button-success" size="sm" />
          <TextComponent variant="muted">{message}</TextComponent>{" "}
        </span>
      )}
      {(() => {
        if (props.disabled && message) {
          return (
            <span className="flex gap-1 text-neutral-medium">
              <TextComponent variant="muted">{message}</TextComponent>
            </span>
          );
        }
        if (!status && message) {
          return (
            <span className="flex gap-1">
              <TextComponent variant="muted">{message}</TextComponent>
            </span>
          );
        }
        return "";
      })()}
    </div>
  </div>
);

Textarea.displayName = "Textarea";

export { Textarea };
